/**
 * Authenticated layout. Side nav on desktop, bottom tabs on mobile.
 * Mirrors iOS TabView pattern but with desktop-native multi-pane room
 * to grow into later.
 */

import type { LucideIcon } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { ICON_STROKE, NavIcons } from "@/shared/components/icons";
import { LogoMark } from "@/shared/components/LogoMark";
import { NearCapBanner } from "@/shared/components/layout/NearCapBanner";
import { TopLeakBanner } from "@/shared/components/layout/TopLeakBanner";
import { UsageChip } from "@/shared/components/layout/UsageChip";
import { LevelChip } from "@/shared/components/layout/LevelChip";
import { useIsAdmin } from "@/features/admin/lib/useIsAdmin";

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
}

// Order is intentional: the active-loop flagships (Drills → Skills) sit
// at the top; supplementary surfaces (Quizzes, Study) follow; utility
// surfaces (Recent, Settings) close it out. Feedback is NOT here — it's
// a rarely-used surface that didn't justify a primary nav slot, so it
// lives as an entry point inside Settings (route still /feedback).
const NAV_ITEMS: NavItem[] = [
  { to: "/drills", label: "Drills", Icon: NavIcons.drills },
  { to: "/skills", label: "Skills", Icon: NavIcons.skills },
  { to: "/tracker", label: "Tracker", Icon: NavIcons.tracker },
  { to: "/analyzer", label: "Analyzer", Icon: NavIcons.analyzer },
  { to: "/quizzes", label: "Quizzes", Icon: NavIcons.quizzes },
  { to: "/study", label: "Study", Icon: NavIcons.study },
  { to: "/recent", label: "Recent", Icon: NavIcons.recent },
  { to: "/settings", label: "Settings", Icon: NavIcons.settings },
];

// Owner-only. Appended to the nav for admins; the /admin route is also
// server-gated, so this is purely about not showing a link nobody else
// can use. See features/admin/lib/useIsAdmin.
const ADMIN_ITEM: NavItem = { to: "/admin", label: "Admin", Icon: NavIcons.admin };

export function AppShell() {
  const location = useLocation();
  const { isAdmin } = useIsAdmin();
  const navItems = isAdmin ? [...NAV_ITEMS, ADMIN_ITEM] : NAV_ITEMS;
  // Banner is a "go drill" CTA — redundant once the user is already
  // in a chat. Also reclaims vertical space so the chat input + tab
  // bar fit cleanly on mobile.
  const showLeakBanner = !location.pathname.startsWith("/chat");

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Desktop side nav */}
      <aside className="hidden md:flex md:flex-col w-56 border-r border-border bg-surface px-4 py-6 sticky top-0 self-start h-screen">
        <div className="text-xl font-semibold mb-8 flex items-center gap-2.5 tracking-tight">
          <LogoMark size={24} />
          <span>Holdem Drills</span>
        </div>
        <nav className="flex flex-col gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition",
                  isActive
                    ? "bg-accent/15 text-text font-medium"
                    : "text-text-muted hover:bg-text/5 hover:text-text",
                ].join(" ")
              }
            >
              <item.Icon size={18} strokeWidth={ICON_STROKE} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        {/* Usage chip pinned to the bottom of the side nav. Renders
         *  null for uncapped tiers, so the nav stays clean for paying
         *  users. */}
        <div className="mt-auto pt-4 space-y-2">
          <LevelChip />
          <UsageChip />
        </div>
      </aside>

      {/* Mobile header. `viewport-fit=cover` extends the viewport
       * under the notch/Dynamic Island — without env(safe-area-inset-top)
       * the brand mark sits behind it on devices that have one.
       * Usage chip sits absolute-positioned at the right edge so the
       * title stays centered for uncapped users (where the chip is
       * null). */}
      <header
        className="md:hidden border-b border-border bg-surface px-4 pb-3 flex items-center justify-center sticky top-0 z-10 relative"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <span className="font-semibold flex items-center gap-2 tracking-tight">
          <LogoMark size={22} />
          <span>Holdem Drills</span>
        </span>
        <div
          className="absolute right-4 flex items-center gap-1.5"
          style={{ top: "calc(0.75rem + env(safe-area-inset-top))" }}
        >
          <LevelChip variant="header" />
          <UsageChip variant="header" />
        </div>
      </header>

      <main className="flex-1 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0 min-w-0">
        {showLeakBanner && <TopLeakBanner />}
        {showLeakBanner && <NearCapBanner />}
        <Outlet />
      </main>

      {/* Mobile bottom tab bar. `pb` lifts content above the iPhone home
       * indicator; the inset is added via env() rather than a fixed value
       * so devices without an indicator (older iPhones, Android) don't
       * get extra dead space. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 border-t border-border bg-surface flex justify-around z-10"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              [
                "flex flex-col items-center gap-1 py-2 flex-1 text-xs transition min-h-[44px]",
                isActive ? "text-accent" : "text-text-muted",
              ].join(" ")
            }
          >
            <item.Icon size={20} strokeWidth={ICON_STROKE} aria-hidden />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
